import { Component, inject, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../material/material.module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Address } from '../../core/generated';
import { CepService } from '../services/cep.service';
import { LocationService, Cidade } from '../services/location.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { MatSelectChange } from '@angular/material/select';

interface Estado {
  nome: string;
  sigla: string;
}

@Component({
    selector: 'app-address-dialog',
    standalone: true,
    templateUrl: './address-dialog.component.html',
    styleUrls: ['./address-dialog.component.scss'],
    imports: [FormsModule, MaterialModule]
})
export class AddressDialogComponent implements OnInit {
    addressForm!: FormGroup;
    deviceService = inject(DeviceDetectorService);
    isMobile = this.deviceService.isMobile();
    isEditMode = false;
    isLoadingCep = false;
    isLoadingCities = false;
    estados: Estado[] = [];
    cidades: Cidade[] = [];

    constructor(
        public dialogRef: MatDialogRef<AddressDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { address?: Address, addresses?: Address[], profileProviderPFId?: number, profileProviderPJId?: number },
        private fb: FormBuilder,
        private cepService: CepService,
        private locationService: LocationService
    ) { 
        this.estados = this.locationService.getEstados();
    }

    ngOnInit(): void {
        this.addressForm = this.fb.group({
            id: [''],
            street: ['', Validators.required],
            number: ['', Validators.required],
            complement: [''],
            district: ['', Validators.required], // bairro
            city: ['', Validators.required],
            state: ['', Validators.required],
            zipCode: ['', [
                Validators.required, 
                Validators.pattern(/^[0-9]{5}-?[0-9]{3}$/) // Valida formato 00000-000 ou 00000000
            ]]
        });

        // Check if we're in edit mode by seeing if an address was provided
        if (this.data && this.data.address) {
            this.isEditMode = true;
            this.addressForm.patchValue(this.data.address);
        }
        
        // Ajusta a lógica de consulta de CEP para funcionar com ou sem formatação
        this.addressForm.get('zipCode')?.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(cep => {
                if (!cep) return;
                
                // Remove caracteres não numéricos para validação
                const cepNumerico = cep.replace(/\D/g, '');
                if (cepNumerico.length === 8) {
                    this.consultarCep(cepNumerico);
                }
            });
        
        // Verificar estado atual e carregar cidades se houver estado selecionado
        const estadoInicial = this.data?.address?.state;
        if (estadoInicial) {
            this.loadCidades(estadoInicial);
        }
    }
    
    onStateChange(event: MatSelectChange): void {
        const uf = event.value;
        // Limpa a cidade selecionada
        this.addressForm.get('city')?.setValue('');
        
        this.loadCidades(uf);
    }
    
    loadCidades(uf: string): void {
        this.isLoadingCities = true;
        this.locationService.getCidadesByUF(uf).subscribe({
            next: (cidades) => {
                this.cidades = cidades;
                this.isLoadingCities = false;
                
                // Se estiver carregando a partir do CEP, tentar selecionar a cidade automaticamente
                const cidadeAtual = this.addressForm.get('city')?.value;
                if (cidadeAtual && this.cidades.some(c => c.nome === cidadeAtual)) {
                    this.addressForm.get('city')?.setValue(cidadeAtual);
                }
            },
            error: () => {
                this.isLoadingCities = false;
                this.cidades = [];
            }
        });
    }

    // Adiciona formatação automática ao CEP
    formatarCep(event: any): void {
        const input = event.target;
        let valor = input.value.replace(/\D/g, '');
        
        if (valor.length > 5) {
            valor = valor.substring(0, 5) + '-' + valor.substring(5);
        }
        
        // Limita ao formato 00000-000
        if (valor.length > 9) {
            valor = valor.substring(0, 9);
        }
        
        // Atualiza o campo sem acionar o detector de mudanças
        input.value = valor;
        
        // Atualiza o form control
        this.addressForm.get('zipCode')?.setValue(valor, { emitEvent: false });
        
        // Se tiver 8 dígitos numéricos, consulta o CEP
        const cepNumerico = valor.replace(/\D/g, '');
        if (cepNumerico.length === 8) {
            this.consultarCep(cepNumerico);
        }
    }

    consultarCep(cep: string) {
        if (this.isLoadingCep) return; // Evita múltiplas chamadas simultâneas
        
        this.isLoadingCep = true;
        this.cepService.consultaCep(cep).subscribe({
            next: (resultado) => {
                this.isLoadingCep = false;
                if (resultado) {
                    // Preenche o estado primeiro
                    this.addressForm.patchValue({
                        street: resultado.logradouro || '',
                        district: resultado.bairro || '',
                        state: resultado.uf || '',
                    });
                    
                    // Carrega as cidades do estado
                    if (resultado.uf) {
                        this.loadCidades(resultado.uf);
                        
                        // Aguarda o carregamento das cidades e então define a cidade
                        setTimeout(() => {
                            this.addressForm.patchValue({
                                city: resultado.localidade || ''
                            });
                        }, 500);
                    }
                    
                    // Foca no campo número após preencher os dados
                    if (resultado.logradouro) {
                        setTimeout(() => {
                            document.getElementById('numero')?.focus();
                        }, 100);
                    }
                }
            },
            error: () => {
                this.isLoadingCep = false;
            }
        });
    }

    createAddress() {
        if (this.addressForm.valid) {
            const addressData = this.addressForm.value;
            this.dialogRef.close(addressData);
        }
    }

    closeDialog() {
        this.dialogRef.close(false);
    }
}
